// Drawer → Versions (acceptance K1–K8): update from Hugging Face, the pending
// switch, and one row per version with its actions.
import { modelServingApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import { modelStatus, pendingSwitchText, shortSha, versionRows } from "./modelServingModel";
import { Badge, Btn, MONO, Notice, Progress, SectionHead } from "./modelServingUi";

const BADGE_TONE = { Active: "green", Downloading: "purple", Ready: "cyan", Failed: "pink" };

export default function ModelDrawerVersions({ model, serving, onDialog, onAct }) {
  const rows = versionRows(model);
  const pending = (model.revisions || []).find((r) => r.id === model.pending_revision_id);
  const running = ["running", "warming"].includes(modelStatus(model));
  const sha = (id) => shortSha(model.revisions.find((r) => r.id === id).sha);   // only called for a listed row

  const onRow = (action, id) => {
    const key = model.key;
    if (action === "cancel") {
      onDialog("confirm", {
        title: "Cancel this update?", actionLabel: "Cancel update",
        body: `Stops downloading ${sha(id)} and deletes what was copied so far. The current version keeps serving.`,
        action: () => modelServingApi.cancelDownload(key, id),
      });
    } else if (action === "delete") {
      onDialog("confirm", {
        title: "Delete this version?", actionLabel: "Delete version",
        body: `Deletes ${sha(id)}'s weights from S3. You won't be able to roll back to it.`,
        action: () => modelServingApi.deleteVersion(key, id),
      });
    } else if (action === "rollback" && running) {
      onDialog("confirm", {
        title: `Roll back to ${sha(id)}?`, actionLabel: "Roll back now",
        body: "The model is running, so rolling back restarts it on that version. Requests wait ~3–5 min while it warms up.",
        action: () => modelServingApi.rollback(key, "now"),
      });
    } else if (action === "rollback") {
      onAct(() => modelServingApi.rollback(key, "now"));
    } else if (action === "switch") {
      onAct(() => modelServingApi.switchVersion(key, id, "now"));
    } else {
      onAct(() => modelServingApi.retryDownload(key, id));
    }
  };

  const LABEL = { cancel: "Cancel", delete: "Delete", rollback: "Roll back to this", switch: "Switch to this", retry: "Retry" };
  const LC = { cancel: "cancel-update-download", delete: "delete-version", rollback: "rollback" };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220, fontSize: 12.5, color: COLORS.textMuted }}>
          Want a newer version from Hugging Face? It downloads alongside the current one; the model keeps serving until
          you switch.
        </div>
        <Btn sm kind="primary" data-lc="update" onClick={() => onDialog("update")}>↑ Update from Hugging Face</Btn>
      </div>

      {pending && (
        <div style={{ marginTop: 12 }}>
          <Notice lc="pending-switch" tone="warn" icon="⏱">
            <b style={{ color: COLORS.text }}>{pendingSwitchText(model, serving)}</b>
            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {model.switch_policy === "when_idle"
                ? (
                  <Btn sm kind="primary" data-lc="switch-now"
                    onClick={() => onAct(() => modelServingApi.switchVersion(model.key, pending.id, "now"))}>Switch now</Btn>
                )
                : <Btn sm kind="primary" onClick={() => onDialog("switch")}>Choose when to switch</Btn>}
              <Btn sm data-lc="cancel-pending-switch"
                onClick={() => onAct(() => modelServingApi.cancelPendingSwitch(model.key))}>Cancel pending switch</Btn>
            </div>
          </Notice>
        </div>
      )}

      <SectionHead title="Versions" count={rows.length} />
      <div style={{ display: "grid", gap: 8 }}>
        {rows.map((r) => (
          <div key={r.id} data-testid={`version-${r.id}`}
            style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "10px 12px", borderRadius: 12,
              border: `1px solid ${r.badge === "Active" ? `${COLORS.green}55` : COLORS.border}` }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                <span style={{ fontFamily: MONO }}>{r.title.split(" · ")[0]}</span>
                {r.title.includes(" · ") && ` · ${r.title.split(" · ").slice(1).join(" · ")}`}
              </div>
              <div data-lc={r.progress != null ? "update-progress" : undefined}
                style={{ fontSize: 11.5, color: r.badge === "Failed" ? COLORS.pink : COLORS.textDim, marginTop: 2 }}>{r.meta}</div>
              {r.progress != null && <Progress pct={r.progress} />}
            </div>
            {r.badge && <Badge label={r.badge} tone={BADGE_TONE[r.badge]} />}
            {r.actions.map((a) => (
              <Btn key={a} sm kind={["cancel", "delete"].includes(a) ? "danger" : "default"} data-lc={LC[a]}
                onClick={() => onRow(a, r.id)}>{LABEL[a]}</Btn>
            ))}
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12, color: COLORS.textDim, marginTop: 14 }}>
        When you switch, the version you switch away from is kept for rollback. Older versions are deleted from S3
        automatically.
      </p>
    </div>
  );
}
